import React from "react";
import { formatCurrency } from "../../utilz/function";

interface SummaryProps {
  customer: any;
  products: any[];
  deliveryInfo: {
    deliveryMethod: string;
    deliveryFee: number;
    deliveryNote: string;
  };
}

function formatCurrency1(value: string | number) {
  const numValue = Number(value);
  if (isNaN(numValue)) return value;
  const [integerPart, decimalPart = "00"] = numValue.toFixed(2).split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `${formattedInteger}.${decimalPart}`;
}

const OrderSummary: React.FC<SummaryProps> = ({
  customer,
  products,
  deliveryInfo,
}) => {
  // Calculate subtotal
  const subtotal = products.reduce((sum, product) => {
    return sum + product.price * product.quantity;
  }, 0);

  // Calculate total
  const total =
    subtotal + (formatCurrency(deliveryInfo.deliveryFee, "NGN", "symbol") || 0);

  return (
    <div className="bg-white rounded-lg">
      <div className="mb-6 border border-[#C9C9C9] rounded-lg">
        <h3 className="text-[14px] text-[#2B2B2B] font-semibold px-6 rounded-tr-lg rounded-tl-lg h-[48px] flex items-center bg-[#F5F5F5]">
          Customer Details
        </h3>
        <div className="rounded-lg flex flex-col">
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-[#4E4E4E]">Customer Name</p>
            <p className="font-medium">{customer?.contactName || "N/A"}</p>
          </div>
          <hr />
          <div className="flex text-center items-center px-6 justify-between h-[72px]">
            <p className="text-sm text-gray-500">Customer Type</p>
            <p className="font-medium capitalize ">
              {customer?.accountType || "N/A"}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 border border-[#C9C9C9] rounded-lg">
        {/* <h3 className="text-[14px] text-[#2B2B2B] font-semibold px-6 rounded-tr-lg rounded-tl-lg h-[48px] flex items-center bg-[#F5F5F5]">
          Order Items
          </h3> */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="text-[14px] text-[#2B2B2B] font-semibold px-6 rounded-tr-lg rounded-tl-lg h-[48px] bg-[#F5F5F5]">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">
                  Item Name
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">
                  Quantity
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">
                  Unit Price
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">
                  Sub Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products
                .filter((product) => product._id)
                .map((product, index) => (
                  <tr key={product.id || index} className="border-b h-[72px]">
                    <td className="px-4 py-2 text-sm">{product.name}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      {product.quantity}
                    </td>
                    <td className="px-4 py-2 text-sm text-right">
                      {formatCurrency(product.price, "NGN", "symbol")}
                    </td>
                    <td className="px-4 py-2 text-sm text-right">
                      {formatCurrency(
                        product.price * product.quantity,
                        "NGN",
                        "symbol"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6 border border-[#C9C9C9] rounded-lg">
        <h3 className="text-[14px] text-[#2B2B2B] font-semibold px-6 rounded-tr-lg rounded-tl-lg h-[48px] flex items-center bg-[#F5F5F5]">
          Payment Details
        </h3>
        <div className="rounded-lg flex flex-col">
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Payment Term</p>
            <p className="font-medium">
              {customer?.creditStatus?.type
                ? customer.creditStatus.type.charAt(0).toUpperCase() +
                  customer.creditStatus.type.slice(1)
                : "N/A"}
            </p>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Available Credit</p>
            <p className="font-medium">
              {formatCurrency(customer?.creditStatus?.limit, "NGN", "symbol") ||
                0}
            </p>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Payment Period</p>
            <p className="font-medium">{customer?.creditStatus?.period || 0}</p>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Payment Due Date</p>
            <p className="font-medium">
              {customer?.creditStatus?.paymentDueDate || "N/A"}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 border border-[#C9C9C9] rounded-lg">
        <h3 className="text-[14px] text-[#2B2B2B] font-semibold px-6 rounded-tr-lg rounded-tl-lg h-[48px] flex items-center bg-[#F5F5F5]">
          Delivery Information
        </h3>
        <div className="rounded-lg flex flex-col">
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Delivery Method</p>
            <p className="font-medium">
              {deliveryInfo.deliveryMethod === "pick-up"
                ? "Pick Up"
                : "Dispatch"}
            </p>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Delivery Fee</p>
            <p className="font-medium">
              ₦{formatCurrency1(deliveryInfo.deliveryFee) || 0}
            </p>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <p className="text-sm text-gray-500">Delivery Notes</p>
            <p className="font-medium">
              {deliveryInfo.deliveryNote || "No notes provided"}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 border border-[#C9C9C9] rounded-lg">
        <div className="rounded-lg flex flex-col">
          <div className="flex px-6 items-center justify-between h-[72px]">
            <span className="font-medium">Sub Total:</span>
            <span className="font-medium">{formatCurrency(subtotal)}</span>
          </div>
          <hr />
          <div className="flex px-6 items-center justify-between h-[72px]">
            <span className="font-medium">Delivery Fee:</span>
            <span className="font-medium">
              ₦ {formatCurrency1(deliveryInfo.deliveryFee) || 0}
            </span>
          </div>
          <hr />
          <div className="text-[14px] text-[#2B2B2B] font-semibold rounded-br-lg rounded-bl-lg h-[48px] flex px-6 items-center justify-between bg-[#F5F5F5]">
            <span className="font-bold text-lg">Total:</span>
            <span className="font-bold text-lg">
              ₦ {formatCurrency1(total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
